import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getParty } from '@/api/parties';
import { sendPo } from '@/api/po';
import { sendDocWhatsApp, sendDocEmail } from '@/api/admin';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import type { Po } from '@/api/po';

interface Props {
  open: boolean;
  po: Po | null;
  onClose: () => void;
}

export function PoSendModal({ open, po, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const { data: vendor, isFetching: vendorLoading, isError: vendorError } = useQuery({
    queryKey: ['party', po?.vendorId],
    queryFn: () => getParty(po!.vendorId),
    enabled: open && !!po,
    // Always refetch when the modal is (re)opened — the user may have just
    // updated the vendor's contact details in Party Master, and stale-time
    // caching would otherwise serve the old (blank) record.
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });

  const phone = vendor?.whatsapp?.trim() || vendor?.mobile?.trim() || '';
  const email = vendor?.email?.trim() || '';
  const hasContact = !!(phone || email);
  const noContact  = !vendorLoading && !!vendor && !hasContact;

  const sendPoMutation = useMutation({ mutationFn: () => sendPo(po!.id) });
  const waMutation     = useMutation({ mutationFn: () => sendDocWhatsApp('po', po!.id, phone) });
  const emailMutation  = useMutation({ mutationFn: () => sendDocEmail('po', po!.id, email) });

  async function handleSend() {
    if (!hasContact) return;
    try {
      await sendPoMutation.mutateAsync();
      qc.invalidateQueries({ queryKey: ['po'] });

      const sends: Promise<unknown>[] = [];
      if (phone) sends.push(waMutation.mutateAsync().catch((e) => toast.push(apiError(e, 'WhatsApp send failed'), 'error')));
      if (email) sends.push(emailMutation.mutateAsync().catch((e) => toast.push(apiError(e, 'Email send failed'), 'error')));
      await Promise.all(sends);

      const channels = [phone ? 'WhatsApp' : null, email ? 'Email' : null].filter(Boolean).join(' + ');
      if (channels) {
        toast.pushStep(`PO sent to vendor via ${channels}`, {
          body: 'Waiting on vendor acknowledgement.',
          href: '/purchase/po',
          nextLabel: 'Open Purchase Orders',
          tag: 'po-sent',
        });
      } else {
        toast.push('PO status updated', 'success');
      }

      onClose();
    } catch (e) {
      toast.push(apiError(e, 'Send failed'), 'error');
    }
  }

  if (!open || !po) return null;
  const isBusy = sendPoMutation.isPending || waMutation.isPending || emailMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.65)' }}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl shadow-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-3"
          style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
          <div>
            <div className="text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}>
              Send PO &mdash; {po.poNo}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              to {po.vendor.name}
            </div>
          </div>
          <button onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg opacity-50 hover:opacity-100"
            style={{ background: 'var(--bg-soft)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-4 px-5 py-4">

          {/* Vendor record couldn't be loaded (deleted / missing) */}
          {vendorError && (
            <div className="flex items-start gap-2 rounded-xl border px-4 py-3"
              style={{ background: '#fff1f0', borderColor: '#ffccc7', color: '#a8071a' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/>
              </svg>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-bold uppercase tracking-wide">
                  Vendor Record Not Found
                </span>
                <span className="text-[11px] font-normal normal-case">
                  This PO references vendor id {po.vendorId} ({po.vendor.name}), but the record is no longer in Party Master. Restore or re-create the vendor to send this PO.
                </span>
              </div>
            </div>
          )}

          {/* No contact error */}
          {noContact && (
            <div className="flex items-center gap-2 rounded-xl border px-4 py-3"
              style={{ background: '#fff1f0', borderColor: '#ffccc7', color: '#a8071a' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/>
              </svg>
              <span className="text-xs font-bold uppercase tracking-wide">
                No Vendor Contact Information Available
              </span>
            </div>
          )}

          {/* Contact info */}
          {vendorLoading && (
            <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>Loading vendor contact&hellip;</div>
          )}
          {!vendorLoading && vendor && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 rounded-lg px-3 py-2"
                style={{ background: phone ? 'var(--bg-card)' : 'var(--bg-soft)', border: '1px solid var(--border-color)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={phone ? '#25d366' : 'var(--text-muted)'} strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 013.09 5.18 2 2 0 015.09 3h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L9.91 10.09a16 16 0 006 6l.46-.46a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 17.92z"/>
                </svg>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>WhatsApp / Mobile</div>
                  <div className="text-[12px]" style={{ color: phone ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {phone || 'Not set in party master'}
                  </div>
                </div>
                {phone && <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: '#d1fae5', color: '#065f46' }}>WILL SEND</span>}
              </div>

              <div className="flex items-center gap-3 rounded-lg px-3 py-2"
                style={{ background: email ? 'var(--bg-card)' : 'var(--bg-soft)', border: '1px solid var(--border-color)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={email ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Email</div>
                  <div className="text-[12px]" style={{ color: email ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {email || 'Not set in party master'}
                  </div>
                </div>
                {email && <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: '#d1fae5', color: '#065f46' }}>WILL SEND</span>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t px-5 py-3"
          style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
          <button onClick={onClose} disabled={isBusy}
            className="rounded-lg px-4 py-1.5 text-xs font-bold"
            style={{ background: 'var(--bg-soft)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isBusy || !hasContact || vendorLoading}
            className="rounded-lg px-5 py-1.5 text-xs font-extrabold text-black disabled:opacity-40"
            style={{ background: 'var(--accent)' }}>
            {isBusy ? 'Sending\u2026' : '\ud83d\udce4 Send PO'}
          </button>
        </div>
      </div>
    </div>
  );
}